// /brand — My Brand profile management.
//
// One brand per user (Phase 1). The user can either auto-fetch the brand
// data from their website (POST /api/scrape-brand → name, brief, logo,
// color palette extracted from the site's CSS) OR fill the form manually.
//
// All field edits auto-save (debounced 600ms for text, immediate on logo
// + color changes). Status pill near the top shows Saving / Saved / Error.
//
// On mount, fetches the user's existing brand via GET /api/brand and
// populates the form. If none saved yet, fields start empty.
//
// Phase 2 (later, NOT now) — wire the saved brand into the wizard's
// /api/suggest-copy + /api/generate prompts so generations automatically
// reflect the user's brand voice + palette + logo.

import { useCallback, useEffect, useRef, useState } from 'react';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import { BrandImageryReview } from '@/components/feature/BrandImageryReview';
import {
  type BrandFontEntry,
  type BrandProfile,
  type ScrapeBrandResult,
  getBrand,
  saveBrand,
  scrapeBrand,
} from '@/lib/api';
import { useBrand } from '@/hooks/useBrand';
import FontSwapModal from '@/pages/brand/components/FontSwapModal';

const BRIEF_MAX = 300;
const COLORS_MAX = 5;
// Match backend's debounce + auto-save semantics. 600ms is short enough
// that "feels live" but long enough that we don't fire on every keystroke.
const TEXT_DEBOUNCE_MS = 600;


type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';


export default function BrandPage() {
  const { activeBrand, refresh: refreshBrandList } = useBrand();
  // --- Form state ---
  const [name, setName] = useState('');
  const [brief, setBrief] = useState('');
  const [colors, setColors] = useState<string[]>([]);
  // Jun 15 — brand fonts. Each entry pairs the detected font (from website
  // scrape) with the Google-Fonts substitute we'll actually render with.
  // The swap modal lets the user pick a different Google Font per entry.
  const [fonts, setFonts] = useState<BrandFontEntry[]>([]);
  const [swapFontIndex, setSwapFontIndex] = useState<number | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);

  // Pending logo base64 — when the user uploads a new file, we hold it
  // here until the next save fires. Once save succeeds the backend
  // returns logoUrl and we clear this. The presence of pendingLogo
  // means "preview from base64", absence means "preview from logoUrl".
  const [pendingLogo, setPendingLogo] = useState<string | null>(null);

  // --- URL-fetch state ---
  const [fetchUrl, setFetchUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // --- Save status ---
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // --- Debounce + in-flight tracking ---
  const debounceRef = useRef<number | null>(null);
  const saveInFlightRef = useRef(false);
  // When a save is requested WHILE one is already running, this flag
  // remembers to fire a follow-up save once the current one finishes.
  // Without this, the user typing during a save (or clicking "Fetch
  // brand" mid-save) would silently drop the new data on the floor.
  const saveQueuedRef = useRef(false);
  // Holds the latest values so the debounced save reads fresh state.
  // We do this via a ref instead of including state in deps so the
  // useCallback identity stays stable and the debounce timer survives
  // re-renders.
  const latestRef = useRef({ name, brief, colors, fonts, sourceUrl, pendingLogo });
  latestRef.current = { name, brief, colors, fonts, sourceUrl, pendingLogo };

  // --- Fetch existing brand on mount ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const existing = await getBrand();
        if (cancelled) return;
        if (existing) {
          setName(existing.name ?? '');
          setBrief(existing.brief ?? '');
          setColors(existing.colors);
          setFonts(existing.fonts ?? []);
          setLogoUrl(existing.logoUrl);
          setSourceUrl(existing.sourceUrl);
          // Pre-fill the URL fetch input from source_url so a re-scrape
          // is one-click.
          if (existing.sourceUrl) setFetchUrl(existing.sourceUrl);
        }
      } catch (err) {
        console.error('Failed to load brand:', err);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);


  // ---------- Save (debounced) ----------

  const performSave = useCallback(async () => {
    // Another save is mid-flight — mark that we need to re-fire when
    // it's done, then bail. The finally block at the end of the
    // currently-running save will re-invoke performSave.
    if (saveInFlightRef.current) {
      saveQueuedRef.current = true;
      return;
    }
    saveInFlightRef.current = true;
    setSaveStatus('saving');
    setSaveError(null);
    const snapshot = latestRef.current;
    try {
      const saved = await saveBrand({
        name: snapshot.name || null,
        brief: snapshot.brief || null,
        colors: snapshot.colors,
        fonts: snapshot.fonts,
        sourceUrl: snapshot.sourceUrl,
        logoBase64: snapshot.pendingLogo,
      });
      // Only swap the local logo state if the user HASN'T set a newer
      // pending logo in the meantime. If they have (e.g. clicked "Fetch
      // brand" while this save was in flight), wiping pendingLogo here
      // would erase that newer logo just before the next save uploads
      // it. Compare current ref to the snapshot we just saved.
      if (latestRef.current.pendingLogo === snapshot.pendingLogo) {
        setLogoUrl(saved.logoUrl);
        setPendingLogo(null);
      }
      setSaveStatus('saved');
      // Fade the "Saved" indicator after 2s.
      window.setTimeout(() => {
        setSaveStatus((s) => (s === 'saved' ? 'idle' : s));
      }, 2000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setSaveError(msg);
      setSaveStatus('error');
    } finally {
      saveInFlightRef.current = false;
      // If another save was requested while we were in flight (text
      // typing, color change, or a Fetch-brand click), fire it now.
      // This catches the data we'd otherwise have lost when performSave
      // returned early up top.
      if (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        void performSave();
      }
    }
  }, []);

  // Bump status to "dirty" + schedule a debounced save. Call this from
  // every field's onChange. We don't fire while initialLoading is true
  // so the mount-fetch doesn't trigger an immediate self-save.
  const queueSave = useCallback((opts?: { immediate?: boolean; override?: Partial<typeof latestRef.current> }) => {
    if (initialLoading) return;
    // `latestRef` only syncs to state on the NEXT render. An immediate save
    // fired right after setState() would read STALE values — so callers that
    // change state + save in the same tick (Fetch brand, logo upload) pass
    // the fresh values here, applied synchronously before performSave reads.
    if (opts?.override) {
      latestRef.current = { ...latestRef.current, ...opts.override };
    }
    setSaveStatus('dirty');
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    if (opts?.immediate) {
      void performSave();
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      void performSave();
    }, TEXT_DEBOUNCE_MS);
  }, [initialLoading, performSave]);

  // Cancel pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);


  // ---------- URL fetch ----------

  const handleFetchFromUrl = useCallback(async () => {
    const url = fetchUrl.trim();
    if (!url) return;
    setFetching(true);
    setFetchError(null);
    try {
      const result: ScrapeBrandResult = await scrapeBrand(url);
      // Unified behavior with the Add-Brand wizard (brands/new.tsx) —
      // an explicit "Fetch brand" click REPLACES the form with whatever
      // the scrape returned, including empty arrays. Previously this
      // page had a `result.colors.length > 0` guard that quietly kept
      // stale colors when the scrape returned an empty palette, but
      // that made the two surfaces inconsistent: the wizard would
      // populate / clear, the My Brand page would only populate.
      // Same backend endpoint (POST /api/scrape-brand), same
      // application logic — now actually one user-facing pattern.
      setName(result.name);
      setBrief(result.brief);
      setColors(result.colors);
      // Jun 15 — fonts come back as detected+substitute pairs from the
      // backend's Google-Fonts substitution. Replace whatever was here
      // before (consistent with how we treat colors above).
      setFonts(result.fonts);
      if (result.logoBase64) {
        setPendingLogo(result.logoBase64);
        // Clear the public URL so the preview switches to the new base64.
        setLogoUrl(null);
      }
      setSourceUrl(result.sourceUrl);
      // Fire an immediate save with the FRESH scraped values (state setters
      // above haven't re-rendered yet, so latestRef is still stale).
      queueSave({
        immediate: true,
        override: {
          name: result.name,
          brief: result.brief,
          colors: result.colors,
          fonts: result.fonts,
          sourceUrl: result.sourceUrl,
          pendingLogo: result.logoBase64 ? result.logoBase64 : latestRef.current.pendingLogo,
        },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch brand from URL';
      setFetchError(msg);
    } finally {
      setFetching(false);
    }
  }, [fetchUrl, queueSave]);


  // ---------- Field handlers ----------

  const handleNameChange = (v: string) => {
    setName(v);
    queueSave();
  };

  const handleBriefChange = (v: string) => {
    // Hard-clamp at the max to give visible feedback (rather than letting
    // them type past and trimming on save).
    setBrief(v.slice(0, BRIEF_MAX));
    queueSave();
  };

  const handleColorChange = (idx: number, v: string) => {
    const next = colors.slice();
    next[idx] = v.toUpperCase();
    setColors(next);
    queueSave();
  };

  const handleColorRemove = (idx: number) => {
    const next = colors.filter((_, i) => i !== idx);
    setColors(next);
    queueSave();
  };

  const handleColorAdd = () => {
    if (colors.length >= COLORS_MAX) return;
    const next = [...colors, '#000000'];
    setColors(next);
    queueSave();
  };

  // Font swap (Jun 15) — user picks a different Google Font for one of
  // the scraped entries. We keep `detected` (the original site font) so
  // the swap is reversible. method becomes 'manual-swap'.
  const handleFontSwap = (idx: number, substitute: string) => {
    const entry = fonts[idx];
    if (!entry) return;
    const next = fonts.slice();
    next[idx] = {
      ...entry,
      substitute,
      method: 'manual-swap',
    };
    setFonts(next);
    setSwapFontIndex(null);
    queueSave({ immediate: true, override: { fonts: next } });
  };

  const handleFontRemove = (idx: number) => {
    const next = fonts.filter((_, i) => i !== idx);
    setFonts(next);
    queueSave({ immediate: true, override: { fonts: next } });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleLogoUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setSaveError('Please pick a PNG, JPG, or WEBP image.');
      setSaveStatus('error');
      return;
    }
    // Convert to base64 data URI.
    const dataUri = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
      reader.readAsDataURL(file);
    });
    setPendingLogo(dataUri);
    setLogoUrl(null);
    queueSave({ immediate: true, override: { pendingLogo: dataUri } });
  };

  const handleLogoRemove = () => {
    setPendingLogo('');  // empty string = explicit delete (backend convention)
    setLogoUrl(null);
    queueSave({ immediate: true, override: { pendingLogo: '' } });
  };

  // Live preview source: pending base64 (just-uploaded) wins, else
  // persisted public URL, else null.
  const previewLogoSrc = pendingLogo || logoUrl;


  return (
    <AppLayoutV2>
      <div className="max-w-[1500px] mx-auto px-6 py-8 lg:py-12">

        {/* ============ Hero header ============ */}
        <div className="flex items-start justify-between gap-4 mb-8 lg:mb-10">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <i className="ri-palette-line text-2xl" />
            </div>
            <div className="flex-1">
              <h1 className="font-display font-bold text-dark text-2xl lg:text-3xl mb-1">
                My Brand
              </h1>
              <p className="text-dark-muted text-sm lg:text-base max-w-xl">
                Set up your brand once. We'll use it to keep every generated
                post on-brand.
              </p>
            </div>
          </div>
          <SaveStatusPill status={saveStatus} error={saveError} />
        </div>

        {initialLoading ? (
          <div className="flex items-center justify-center py-20 text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
            Loading your brand…
          </div>
        ) : (
          <div className="space-y-6">

            {/* ============ Quick start: URL fetch ============ */}
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <i className="ri-flashlight-line text-base" />
                </div>
                <div className="flex-1">
                  <h2 className="font-display font-bold text-dark text-base mb-0.5">
                    Quick start — fetch from your website
                  </h2>
                  <p className="text-xs text-dark-muted">
                    We'll auto-fill the form below with your logo, name,
                    brand description, and color palette extracted from your
                    site's CSS. Existing fields you've edited won't be
                    overwritten.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={fetchUrl}
                  onChange={(e) => setFetchUrl(e.target.value)}
                  placeholder="https://yourbrand.com"
                  className="flex-1 px-4 py-2.5 rounded-full bg-surface border border-surface-dark/40 text-sm text-dark placeholder:text-dark-muted/60 focus:outline-none focus:border-primary/40"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !fetching) handleFetchFromUrl();
                  }}
                />
                <button
                  onClick={handleFetchFromUrl}
                  disabled={fetching || !fetchUrl.trim()}
                  className="
                    w-full sm:w-auto justify-center
                    inline-flex items-center gap-2 px-5 py-2.5 rounded-full
                    bg-primary hover:bg-primary-hover text-white text-sm font-bold
                    shadow-md shadow-primary/25 transition-all cursor-pointer
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  {fetching ? (
                    <><i className="ri-loader-4-line animate-spin" />Fetching…</>
                  ) : (
                    <><i className="ri-magic-line" />Fetch brand</>
                  )}
                </button>
              </div>
              {fetchError && (
                <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">
                  <strong>Could not fetch:</strong> {fetchError}
                </div>
              )}
            </div>

            {/* ============ Brand details — one consolidated panel ============
                Layout:
                  TOP ROW (md+ side-by-side, stacks below md):
                    LEFT  — large logo preview + upload/remove
                    RIGHT — brand name (top) + brief description (below)
                  DIVIDER
                  BOTTOM ROW: color palette swatches inline                                                            */}
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-5 lg:p-6 space-y-5 lg:space-y-6">

              {/* ---- Top row: logo + (name/brief) ---- */}
              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5 lg:gap-6">

                {/* LEFT: Logo */}
                <div>
                  <label className="block font-display font-bold text-dark text-base mb-2">
                    Logo
                  </label>
                  {/* Square 192×192 preview tile — clickable to open the
                      file picker. Show a placeholder icon when empty. */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title={previewLogoSrc ? 'Click to replace logo' : 'Click to upload a logo'}
                    className="
                      group relative w-full max-w-[200px] aspect-square rounded-2xl
                      border-2 border-dashed border-dark-muted/30
                      bg-surface/40 flex items-center justify-center overflow-hidden
                      hover:border-primary/50 hover:bg-surface/70
                      transition-colors cursor-pointer
                    "
                  >
                    {previewLogoSrc ? (
                      <img
                        src={previewLogoSrc}
                        alt="Brand logo"
                        className="w-full h-full object-contain p-3"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-dark-muted">
                        <i className="ri-image-add-line text-4xl" />
                        <span className="text-xs font-semibold">Upload logo</span>
                      </div>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleLogoUpload(file);
                      // Reset so picking the same file twice still fires.
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                  {/* Action row under the tile */}
                  <div className="flex gap-2 mt-3 max-w-[200px]">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="
                        flex-1 inline-flex items-center justify-center gap-1.5
                        px-3 py-1.5 rounded-full border border-dark-muted/40
                        hover:border-primary/40 hover:text-primary
                        text-xs font-semibold text-dark transition-colors cursor-pointer
                      "
                    >
                      <i className="ri-upload-cloud-line" />
                      {previewLogoSrc ? 'Replace' : 'Upload'}
                    </button>
                    {previewLogoSrc && (
                      <button
                        onClick={handleLogoRemove}
                        title="Remove logo"
                        className="
                          inline-flex items-center justify-center
                          w-8 h-8 rounded-full
                          text-red-500 hover:bg-red-50
                          transition-colors cursor-pointer
                        "
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-dark-muted mt-2 max-w-[200px]">
                    PNG, JPG, or WEBP. Used to mark generated posts.
                  </p>
                </div>

                {/* RIGHT: Name + Brief stacked vertically */}
                <div className="space-y-5">
                  {/* Brand name */}
                  <div>
                    <label className="block font-display font-bold text-dark text-base mb-2">
                      Brand name
                      <span className="text-primary ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="e.g. Acme Skincare"
                      maxLength={120}
                      className="
                        w-full px-4 py-2.5 rounded-xl bg-white
                        border border-dark-muted/40 text-base text-dark
                        placeholder:text-dark-muted/60
                        focus:outline-none focus:border-primary
                      "
                    />
                  </div>

                  {/* Brief description */}
                  <div>
                    <div className="flex items-baseline justify-between mb-2">
                      <label className="block font-display font-bold text-dark text-base">
                        Brief description
                      </label>
                      <span className="text-xs text-dark-muted">
                        {brief.length}/{BRIEF_MAX}
                      </span>
                    </div>
                    <textarea
                      value={brief}
                      onChange={(e) => handleBriefChange(e.target.value)}
                      placeholder="What does your brand do? Who's it for? What's the vibe?"
                      rows={4}
                      maxLength={BRIEF_MAX}
                      className="
                        w-full px-4 py-2.5 rounded-xl bg-white
                        border border-dark-muted/40 text-sm text-dark
                        placeholder:text-dark-muted/60
                        focus:outline-none focus:border-primary resize-none
                      "
                    />
                    <p className="text-xs text-dark-muted mt-1">
                      Brand-voice context for AI-generated post copy.
                    </p>
                  </div>
                </div>
              </div>

              {/* ---- Divider ---- */}
              <div className="border-t border-surface-dark/20" />

              {/* ---- Bottom row: color palette ---- */}
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="block font-display font-bold text-dark text-base">
                    Color palette
                  </label>
                  <span className="text-xs text-dark-muted">
                    {colors.length}/{COLORS_MAX}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {colors.map((c, i) => (
                    <ColorSwatch
                      key={i}
                      color={c}
                      onChange={(v) => handleColorChange(i, v)}
                      onRemove={() => handleColorRemove(i)}
                    />
                  ))}
                  {colors.length < COLORS_MAX && (
                    // Wrapped in the same flex-col + invisible hex-label
                    // pattern as ColorSwatch so the + button has the
                    // SAME total height. Without this, the swatch (tile
                    // + hex label) is ~16px taller than a bare button
                    // and the row's `items-center` pushes the + to the
                    // mid-line of the swatch instead of aligning tiles.
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={handleColorAdd}
                        className="
                          w-14 h-14 rounded-xl border-2 border-dashed border-dark-muted/40
                          hover:border-primary/40 hover:text-primary
                          flex items-center justify-center text-dark-muted
                          transition-colors cursor-pointer
                        "
                        title="Add a color"
                      >
                        <i className="ri-add-line text-xl" />
                      </button>
                      {/* Invisible label spacer to match ColorSwatch's hex
                          label height. aria-hidden + select-none so it's
                          truly visual filler only. */}
                      <span
                        aria-hidden="true"
                        className="text-[10px] font-mono select-none invisible"
                      >
                        #000000
                      </span>
                    </div>
                  )}
                  {colors.length === 0 && (
                    <span className="text-xs text-dark-muted ml-1">
                      No colors yet — fetch from URL or click + to add manually.
                    </span>
                  )}
                </div>
                <p className="text-xs text-dark-muted mt-2">
                  Up to {COLORS_MAX} brand colors. Used to match palettes in future generated posts.
                </p>
              </div>

              {/* ---- Divider ---- */}
              <div className="border-t border-surface-dark/20" />

              {/* ---- Fonts (Jun 15 — Google-Fonts-only substitution) ---- */}
              <div>
                <div className="flex items-baseline justify-between mb-1">
                  <label className="block font-display font-bold text-dark text-base">
                    Brand fonts
                  </label>
                  <span className="text-xs text-dark-muted">{fonts.length} detected</span>
                </div>
                <p className="text-xs text-dark-muted mb-3">
                  We can only render with Google Fonts (commercial fonts aren't
                  licensed for our outputs). Each detected font is paired with
                  its closest Google Fonts match — click "Change" to pick a
                  different one.
                </p>

                {fonts.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-dark-muted/30 px-4 py-6 text-xs text-dark-muted text-center">
                    No fonts detected yet — fetch from URL above to populate.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {fonts.map((f, i) => (
                      <FontRow
                        key={`${f.detected}-${i}`}
                        entry={f}
                        onChange={() => setSwapFontIndex(i)}
                        onRemove={() => handleFontRemove(i)}
                      />
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* ============ Brand imagery (Brand DNA source) ============ */}
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-5 lg:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <i className="ri-camera-lens-line text-base" />
                </div>
                <div className="flex-1">
                  <h2 className="font-display font-bold text-dark text-base mb-0.5">
                    Brand imagery
                  </h2>
                  <p className="text-xs text-dark-muted">
                    Campaign and lifestyle photography pulled from your site. We'll
                    build your Brand DNA — font, color, spacing, styling — from the
                    shots you keep, and use it to style future creatives. Set aside
                    or delete anything off-brand.
                  </p>
                </div>
              </div>
              <BrandImageryReview />
            </div>

          </div>
        )}

        {/* Font swap modal — Jun 15. Mounted at the root so its overlay
            covers everything. Defers Google-Fonts catalog fetch until
            opened (lazy load + session cache). */}
        {swapFontIndex !== null && fonts[swapFontIndex] && (
          <FontSwapModal
            currentSubstitute={fonts[swapFontIndex].substitute}
            detected={fonts[swapFontIndex].detected}
            onPick={(family) => handleFontSwap(swapFontIndex, family)}
            onClose={() => setSwapFontIndex(null)}
          />
        )}
      </div>
    </AppLayoutV2>
  );
}


// =============================================================================
// FontRow — one detected font + its Google substitute + Change / Remove.
// Renders each name in its OWN family so the user sees what they're picking.
// =============================================================================

function FontRow({
  entry,
  onChange,
  onRemove,
}: {
  entry: BrandFontEntry;
  onChange: () => void;
  onRemove: () => void;
}) {
  const methodLabel = (() => {
    switch (entry.method) {
      case 'exact-google': return 'Already a Google Font';
      case 'fast-path': return 'Closest match';
      case 'llm': return 'AI-classified';
      case 'manual-swap': return 'You picked this';
      default: return 'Fallback';
    }
  })();
  const isExact = entry.detected.toLowerCase() === entry.substitute.toLowerCase();
  return (
    <div className="flex items-center gap-3 rounded-xl bg-surface border border-surface-dark/30 px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="text-base text-dark truncate"
            style={{ fontFamily: `"${entry.substitute}", sans-serif` }}
            title={`Rendering with: ${entry.substitute}`}
          >
            {entry.substitute}
          </span>
          {!isExact && (
            <span className="text-xs text-dark-muted truncate">
              originally{' '}
              <span
                className="italic"
                style={{ fontFamily: `"${entry.detected}", "${entry.substitute}", sans-serif` }}
              >
                {entry.detected}
              </span>
            </span>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-dark-muted/80 mt-0.5 block">
          {methodLabel}
        </span>
      </div>
      <button
        onClick={onChange}
        className="
          text-xs font-semibold text-primary px-3 py-1.5 rounded-full
          border border-primary/30 hover:bg-primary/5 transition-colors cursor-pointer flex-shrink-0
        "
        title="Pick a different Google Font"
      >
        Change
      </button>
      <button
        onClick={onRemove}
        className="
          w-7 h-7 rounded-full text-dark-muted hover:text-red-500
          flex items-center justify-center transition-colors cursor-pointer flex-shrink-0
        "
        title="Remove this font"
        aria-label="Remove font"
      >
        <i className="ri-close-line text-base" />
      </button>
    </div>
  );
}


// =============================================================================
// SaveStatusPill — top-right indicator. Shows "Saving…" while a POST is
// in flight, "Saved" briefly after success, "Save failed" + retry tooltip
// on error. Hides itself when idle to keep the UI quiet.
// =============================================================================

function SaveStatusPill({
  status,
  error,
}: {
  status: SaveStatus;
  error: string | null;
}) {
  if (status === 'idle') return null;
  const label = (() => {
    switch (status) {
      case 'dirty':  return 'Unsaved…';
      case 'saving': return 'Saving…';
      case 'saved':  return 'Saved';
      case 'error':  return 'Save failed';
      default:       return '';
    }
  })();
  const icon = (() => {
    switch (status) {
      case 'dirty':  return 'ri-time-line';
      case 'saving': return 'ri-loader-4-line animate-spin';
      case 'saved':  return 'ri-check-line';
      case 'error':  return 'ri-error-warning-line';
      default:       return '';
    }
  })();
  const tone = (() => {
    switch (status) {
      case 'saved': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'error': return 'bg-red-50 text-red-700 border-red-200';
      default:      return 'bg-surface text-dark-muted border-surface-dark/40';
    }
  })();
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${tone}`}
      title={error ?? undefined}
    >
      <i className={icon} />
      {label}
    </span>
  );
}


// =============================================================================
// (FormCard was dropped when the form moved to a unified single-panel
// layout — each field now inlines its own label + hint markup.)
//
// ColorSwatch — a single color in the palette row. The swatch is a native
// `<input type="color">` styled to look like a chip; the hex label sits
// below. Hover reveals an × to remove.
// =============================================================================

function ColorSwatch({
  color,
  onChange,
  onRemove,
}: {
  color: string;
  onChange: (v: string) => void;
  onRemove: () => void;
}) {
  return (
    <div className="group relative">
      <div className="flex flex-col items-center gap-1">
        {/* Swatch — click anywhere to open the native picker. The picker
            UI varies by browser but always returns a hex string. */}
        <div className="relative w-14 h-14 rounded-xl border border-dark-muted/40 overflow-hidden cursor-pointer">
          <input
            type="color"
            value={color}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
            title="Click to change"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundColor: color }}
          />
        </div>
        <span className="text-[10px] font-mono text-dark-muted uppercase tracking-wide">
          {color}
        </span>
      </div>
      {/* Remove × — appears on hover */}
      <button
        onClick={onRemove}
        title="Remove this color"
        className="
          absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-dark/80 text-white
          flex items-center justify-center opacity-0 group-hover:opacity-100
          transition-opacity cursor-pointer hover:bg-red-500
        "
      >
        <i className="ri-close-line text-[10px]" />
      </button>
    </div>
  );
}
